import { Injectable } from "@angular/core";
import {
  CalculatorOption,
  CalculatorRequest,
  CalculatorUsFormData,
} from "../components/calculator/calculator-us/calculator-us-formData";

import { UsCalculatorResult } from "../model/calculation/calculationResult";
import { environment } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { formatMessage } from "devextreme/localization";
import { JobSpecification } from "../model/calculator-us-remastered/jobSpecification";
import { GeneralInformation } from "../model/calculator-us-remastered/generalInformation";
import {
  ComponentSpecification,
  EndsheetSpecification,
  InsertSpecification,
  JacketCoverSpecification,
  TipInSpecification,
} from "../model/calculator-us-remastered/componentSpecification";
import { PaperSpecification } from "../model/calculator-us-remastered/paperSpecification";
import { Subject } from "rxjs";
import { CaseTypeSpecification } from "../model/calculator-us-remastered/caseTypeSpecification";
import { OptionService } from "./option.service";
import { BinderySpecification } from "../model/calculator-us-remastered/binderySpecification";

export interface PaperWeightCalculationData {
  paperWeight1?: number;
  paperWeight2?: number;
  paperWeight3?: number;
  paperDisplayName?: string;
}

@Injectable({
  providedIn: "root",
})
export class CalculatorUsService {
  constructor(private http: HttpClient, private optionService: OptionService) {}
  private paperWeightCalculationSubject =
    new Subject<PaperWeightCalculationData>();
  public paperWeightCalculation$ =
    this.paperWeightCalculationSubject.asObservable();

  async calculateUs(
    jobSpecification: JobSpecification,
    generalInformation: GeneralInformation,
    calculatorComponents: ComponentSpecification,
    paperSpecification: PaperSpecification,
    caseTypeSpecification: CaseTypeSpecification,
    binderySpecification: BinderySpecification
  ) {
    const calculationData = <CalculatorUsFormData>(<unknown>{
      date: Date.parse(generalInformation.date),
      author: jobSpecification.author,
      title: jobSpecification.title,
      publisher: generalInformation.publisher,
      customerContact: generalInformation.customerContact,
      bpgContact: generalInformation.bpgContact,
      printNumber: "",
      quantity: [
        +jobSpecification.quantity1,
        +jobSpecification.quantity2,
        +jobSpecification.quantity3,
      ],
      product: jobSpecification.product,
      binding: this.getBinding(jobSpecification.product),
      pressMethod: this.getPressMethod(jobSpecification.product),
      edition: this.getEdition(jobSpecification.product),
      width: jobSpecification.width,
      height: jobSpecification.height,
      pages: jobSpecification.pages,
      reprint: jobSpecification.initialReprint != "INITIAL",
      cartonOrPacks: "",
      // paper: "",
      // paperSpecs: "",
      // paperInLb: 0,
      numberOfTitles: 1,
      // trimSizeGroup: "",
      selectedOptions: [],
    });

    const additionalOptionIds: string[] = [];
    // Add the paper specification endsheet option
    if (paperSpecification?.endSheetOptionId) {
      additionalOptionIds.push(paperSpecification.endSheetOptionId);
    }
    // Add the case type specification board material option
    if (caseTypeSpecification?.boardMaterialOptionId) {
      additionalOptionIds.push(caseTypeSpecification.boardMaterialOptionId);
    }
    // Add the case type specification case material option
    if (caseTypeSpecification?.caseMaterialOptionId) {
      additionalOptionIds.push(caseTypeSpecification.caseMaterialOptionId);
    }
    // Add the case type specification spine stamping
    if (
      caseTypeSpecification?.spineStampingOptionId &&
      caseTypeSpecification?.spineStampingOptionValue
    ) {
      // Don't send an option if the value is not a positive number.
      // This can result in a total of 0.0 which will break the edit for frontend response.
      additionalOptionIds.push(caseTypeSpecification.spineStampingOptionId);
    }
    // Add the carton packs options
    if (
      binderySpecification?.cartonsPackOptionId &&
      binderySpecification.cartonsPackValue
    ) {
      additionalOptionIds.push(binderySpecification.cartonsPackOptionId);
    }

    // special treatment for the spine stamping and cartons pack
    if (additionalOptionIds.length > 0) {
      const additionalOptions = await this.convertToCalculatorOptions(
        additionalOptionIds
      );

      // adapt the spine stamping option
      if (
        caseTypeSpecification?.spineStampingOptionId &&
        caseTypeSpecification?.spineStampingOptionValue != undefined
      ) {
        const spineStampingOption = additionalOptions.find(
          (option) =>
            caseTypeSpecification?.spineStampingOptionId === option.optionId
        );
        if (spineStampingOption) {
          spineStampingOption.optionValue =
            caseTypeSpecification.spineStampingOptionValue;
        }
      }

      // adapt the cartons pack option
      if (
        binderySpecification?.cartonsPackOptionId &&
        binderySpecification?.cartonsPackValue != undefined
      ) {
        const cartonsPackOption = additionalOptions.find(
          (option) =>
            binderySpecification?.cartonsPackOptionId === option.optionId
        );
        if (cartonsPackOption) {
          cartonsPackOption.optionValue = binderySpecification.cartonsPackValue;
        }
      }

      calculationData.selectedOptions.push(...additionalOptions);
    }

    const calculationRequest: CalculatorRequest = new CalculatorRequest(
      calculationData,
      generalInformation.publisher?.id || ""
    );

    const calculatorComponentRequest =
      this.convertToCalculatorComponentRequest(calculatorComponents);
    const requestWithComponents: any =
      calculatorComponentRequest === undefined
        ? {
            ...calculationRequest,
          }
        : {
            ...calculationRequest,
            components: calculatorComponentRequest,
          };

    const calcResult = await this.http
      .post<UsCalculatorResult>(
        `${environment.baseUrl}/api/calculator/us/calculate/v2`,
        requestWithComponents // Daten der Calculation
      )
      .toPromise();

    // Call the new paper weight calculation endpoint
    this.calculatePaperWeight(
      jobSpecification,
      generalInformation.publisher?.id,
      paperSpecification?.paperType
    ).catch((err) => {
      console.warn("Paper weight calculation failed:", err);
    });

    if (calcResult?.status !== "success") {
      const errorText = calcResult?.data ? JSON.stringify(calcResult.data) : "";
      const errorMessage = formatMessage(
        "calculator.calculation.error",
        errorText
      );
      throw Error(errorMessage);
    }

    const calcResponse = await this.http
      .post<UsCalculatorResult>(
        `${environment.baseUrl}/api/calculator/us/edit-response`,
        calcResult // daten der response
      )
      .toPromise();

    return calcResponse;
  }

  private async convertToCalculatorOptions(optionIds: string[]) {
    if (!optionIds || optionIds.length === 0) {
      return [];
    }

    const additionalOptions = await this.optionService
      .findOptionMappingsByOptionIds(optionIds)
      .toPromise();
    return additionalOptions.map(
      (optionMapping) =>
      (<CalculatorOption>{
          optionId: optionMapping.optionId,
          specification: optionMapping.specification,
          wildcard: optionMapping.wildcard,
      })
    );
  }

  private async calculatePaperWeight(
    jobSpecification: JobSpecification,
    publisherId?: string,
    paperConfigurationId?: string
  ) {
    if (
      paperConfigurationId &&
      !paperConfigurationId.match(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
      )
    ) {
      console.log(
        "Paper weight calculation skipped: Invalid paperConfigurationId",
        paperConfigurationId
      );
      return;
    }

    if (!publisherId || !paperConfigurationId) {
      console.log(
        "Paper weight calculation skipped: Missing publisherId or paperConfigurationId"
      );

      console.log("Job specification:", {
        width: jobSpecification.width,
        height: jobSpecification.height,
        pages: jobSpecification.pages,
        quantity: jobSpecification.quantity1,
        publisherId,
        paperConfigurationId,
      });
      return;
    }

    const editionValue = this.getEdition(jobSpecification.product);
    const reprint = jobSpecification.initialReprint !== "INITIAL";
    const baseRequest = {
      publisherId,
      paperConfigurationId,
      width: jobSpecification.width,
      height: jobSpecification.height,
      pages: jobSpecification.pages,
      edition: editionValue,
      reprint,
    };

    const qty1 = +jobSpecification.quantity1;
    const qty2 = +jobSpecification.quantity2;
    const qty3 = +jobSpecification.quantity3;

    const makeRequest = (qty: number) =>
      this.http
        .post<any>(`${environment.baseUrl}/api/calculator/paper-weight`, {
          ...baseRequest,
          quantity: qty,
        })
        .toPromise();

    // Collect non-zero quantities in order to match the positional columns of the price table.
    // The price table uses ancillaryItemGroupList indices (0,1,2) which only count provided
    // quantities. If qty2 is missing but qty3 is present, the second column maps to qty3,
    // so paperWeight2 must hold qty3's result (not paperWeight3).
    const quantities = [qty1, qty2, qty3].filter(qty => qty > 0);

    if (quantities.length === 0) {
      this.paperWeightCalculationSubject.next({});
      return;
    }

    console.log("Calling paper weight calculation with:", { ...baseRequest, quantities });

    try {
      const results = await Promise.all(quantities.map(qty => makeRequest(qty)));

      console.log("Paper weight calculation results:", results);

      if (results[0]?.paperWeight) {
        const calculationData: PaperWeightCalculationData = {
          paperWeight1: results[0]?.paperWeight,
          paperWeight2: results[1]?.paperWeight,
          paperWeight3: results[2]?.paperWeight,
          paperDisplayName: results[0].paperDisplayName,
        };
        this.paperWeightCalculationSubject.next(calculationData);
      }
    } catch (error: any) {
      console.error("Paper weight calculation error:", error);
    }
  }

  private convertToCalculatorComponentRequest(
    calculatorComponents: ComponentSpecification
  ) {
    const jacket = this.convertJacketCoverComponent(
      calculatorComponents.jacket
    );
    const cover = this.convertJacketCoverComponent(
      calculatorComponents.cover
    );

    const endsheet = this.convertEndsheetComponent(
      calculatorComponents.endsheet
    );
    const inserts = this.convertInsertComponents(calculatorComponents.inserts);
    const tipIns = this.convertTipInComponents(calculatorComponents.tipIns);

    return {
      jacket,
      cover,
      endsheet,
      inserts,
      tipIns,
    };
  }

  private convertEndsheetComponent(endsheetComponent: EndsheetSpecification) {
    const endsheetFrontColor = endsheetComponent?.frontColor?.split("/")[0];
    const endsheetBackColor = endsheetComponent?.backColor;
    const endsheetColor =
      endsheetFrontColor && endsheetBackColor
        ? `${endsheetFrontColor}/${endsheetBackColor}`
        : undefined;

    return endsheetComponent
      ? {
          plates: endsheetColor ? { color: endsheetColor } : undefined,
          print: endsheetColor ? { color: endsheetColor } : undefined,
          stock: endsheetComponent.paper ? { type: endsheetComponent.paper } : undefined,
        }
      : undefined;
  }

  private convertInsertComponents(insertSpecifications: InsertSpecification[]) {
    return insertSpecifications.map((insertComponent) => ({
      plates: insertComponent.color
        ? { color: insertComponent.color }
        : undefined,
      print:
        insertComponent.color && insertComponent.pages
          ? {
              color: insertComponent.color,
              pages: insertComponent.pages,
            }
          : undefined,
      stock:
        insertComponent.color && insertComponent.pages && insertComponent.paper
          ? {
              color: insertComponent.color,
              pages: insertComponent.pages,
              type: insertComponent.paper,
            }
          : undefined,
    }));
  }

  private convertTipInComponents(tipInSpecifications: TipInSpecification[]) {
    return tipInSpecifications.map((tipInComponent) => ({
      plates: tipInComponent.color
        ? { color: tipInComponent.color }
        : undefined,
      print: tipInComponent.color
        ? {
            color: tipInComponent.color,
          }
        : undefined,
      stock: tipInComponent.paper
          ? {
              type: tipInComponent.paper,
            }
          : undefined,
    }));
  }

  private convertJacketCoverComponent(
    jacketCoverComponent: JacketCoverSpecification | undefined
  ) {
    if (jacketCoverComponent === undefined) {
      return undefined;
    }
    const jacketCoatingSpecialEffects = jacketCoverComponent.specialEffects;
    const jacketCoatingFinishing = jacketCoverComponent.finishing;
    const jacketCoating: any[] = [];
    if (jacketCoatingFinishing) {
      jacketCoating.push({ finishingType: jacketCoatingFinishing });
    }
    jacketCoatingSpecialEffects
      .filter((se) => se !== "NONE")
      .forEach((se) => jacketCoating.push({ specialEffects: se }));

    const jacketFrontColor = jacketCoverComponent.frontColor?.split("/")[0];
    const jacketBackColor = jacketCoverComponent.backColor;
    const jacketColor =
      jacketFrontColor && jacketBackColor
        ? `${jacketFrontColor}/${jacketBackColor}`
        : undefined;

    const jacketPaper = jacketCoverComponent.jacketPaper;

    return {
      coating: jacketCoating,
      plates: jacketColor ? { color: jacketColor } : undefined,
      print: jacketColor ? { color: jacketColor } : undefined,
      stock: jacketPaper ? { type: jacketPaper } : undefined,
    };
  }

  private getPressMethod(product: string | undefined): string | undefined {
    // from the UI we only have TRADE
    if (!product) return "";
    //Mass Market
    // if (product.edition === "MM") {
    //   //Rack
    //   return product.produktType;
    // }
    return undefined;
  }

  private getBinding(product: string | undefined): string {
    if (!product) return "";
    // from the UI we only have TRADE
    //Mass Market
    // if (product.edition === "MM") {
    //   //Rack
    //   if (
    //     product.width == 4.125 &&
    //     (product.height == 6.75 || product.height == 6.625)
    //   ) {
    //     return "MMR";
    //   }
    //   //Premium
    //   if (product.width == 4.125 && product.height == 7.5) {
    //     return "MMP";
    //   }
    //   //Digest
    //   if (product.width == 5.1875 && product.height == 7.625) {
    //     return "MMD";
    //   }
    // } else {
    //   //Trade Market
    //   return product.produktType;
    // }
    return product.startsWith("HARDCOVER")
      ? "TRH"
      : product.startsWith("SOFTCOVER")
      ? "TRS"
      : "";
  }

  private getEdition(product: string | undefined): string {
    if (!product) return "";
    // from the UI we only have TRADE
    return "TRD";
  }
}
